'use strict'
const axios = require('axios')
const legacyNodeConfig = require('./config/config')
const serviceDiscovery = require('@pcln/service-disco')
const abstractLogger = require('abstract-logging') // Abstract implementation, is replaced by user specified instance
const isNode = require('./isNode.js')
const getXPclnHeaders = require('./get-x-pcln-headers.js')

const axiosConfig = {
  timeout: 60000,
}

const deprecationNotices = {}

// Bypass self-signed cert warnings when running in node
// istanbul ignore else
if (isNode) {
  const https = require('https')
  const http = require('http')
  axiosConfig.httpsAgent = new https.Agent({
    rejectUnauthorized: false,
    keepAlive: true,
  })
  axiosConfig.httpAgent = new http.Agent({
    keepAlive: true,
  })
}

const axiosClient = axios.create(axiosConfig)

function cleanSlashes(string) {
  return string.replace(/(^\/)|(\/$)/g, '')
}

function legacyResolveServiceUrl(service, endpoint, logger) {
  let uri = service.uri
  const _f5Type = service.f5Type

  // service-specific request settings
  let base = service.secure ? legacyNodeConfig.baseUrlSecure : legacyNodeConfig.baseUrl
  if (base instanceof Object) {
    base = base[_f5Type]
  }

  /* istanbul ignore next */
  if (!base || base === '/') {
    logger.warn('URL domain not defined.  Check env vars BOOTSTRAP_ENV and BOOTSTRAP_DC or @pcln/config')
  }

  // Replace starting and ending slashes to avoid repeats
  uri = cleanSlashes(base) + '/' + cleanSlashes(uri)
  // strip out non-alphanumeric/'_'/'-' characters from API endpoint
  // istanbul ignore else
  if (endpoint) {
    uri = uri + '/' + cleanSlashes(endpoint)
  }
  return uri
}

function pclnRequest(config = {}, incomingReq = {}) {
  // Merge user config with defaults
  const options = {
    discovery: true,
    logRequestBody: null,
    serviceName: '',
    endpoint: '',
    url: '',
    headers: {},
    ...config,
  }

  // Get all x-pcln- headers from incoming request to pass them forward.
  // If incomingReq.headers contains x-pcln- headers that are also being explicitly set in the options.headers,
  // defer to the values in options.headers. That's why we put the spreads in the order below.
  const xPclnHeaders = getXPclnHeaders(incomingReq)
  if (Object.getOwnPropertyNames(xPclnHeaders).length > 0) {
    options.headers = {
      ...xPclnHeaders,
      ...options.headers,
    }
  }

  const { logger = abstractLogger } = config

  if (!options.url && !deprecationNotices[options.serviceName]) {
    logger.warn(
      `Deprecation warning: the use of 'serviceName' and 'endpoint' in @pcln/request is deprecated and will be removed in a future release.  Please specify a 'url' to connect to`
    )
    deprecationNotices[options.serviceName] = true
  }

  const baseUrl = options.serviceName ? process.env[`SERVICE_${options.serviceName.toUpperCase()}_URL`] : null

  if (baseUrl) {
    options.url = `${cleanSlashes(baseUrl)}/${cleanSlashes(options.endpoint)}`
  } else if (!options.url) {
    // If url is not passed in, try to do url resolution
    if (options.discovery) {
      // When using service discovery, endPoint must include the full route e.g. pws/v0/driveql/
      // Does not support unsecured (http) services
      // Does not have hard-coded timeouts per service
      const hostname = serviceDiscovery(options.serviceName)
      options.url = `https://${cleanSlashes(hostname)}/${cleanSlashes(options.endpoint)}`
    } else {
      // When using legacy url resolution, the basename is prefixed to the endPoint optiom
      // So i.e. do not include "/pws/v0/servicename" in the endPoint option
      const service = legacyNodeConfig.services[options.serviceName]
      if (service) {
        options.url = legacyResolveServiceUrl(service, options.endpoint, logger)
        // istanbul ignore else
        if (!options.timeout) {
          options.timeout = service.timeout
        }
      } else {
        const message = `Service not found in node config (legacy url resolution): ${options.serviceName}`
        logger.error({ error: message, serviceName: options.serviceName }, 'pcln request finished')
        return Promise.reject(new Error(message))
      }
    }
  }

  // Copy options object to log data object
  const logData = {
    serviceName: options.serviceName,
    endpoint: options.endpoint,
    url: options.url,
    timeout: options.timeout || axiosConfig.timeout,
  }
  // Only allow logging request body as development debug feature
  // Do not allow this in production environment
  if (isNode && process.env.NODE_ENV === 'development' && options.logRequestBody === true && options.data) {
    logData.body = options.data
  }

  const startTime = Date.now()

  return new Promise((resolve, reject) => {
    axiosClient
      .request(options)
      .then((response) => {
        const elapsedTime = Date.now() - startTime
        const statusCode = response && response.status
        logger.info({ elapsedTime, statusCode, ...logData }, 'pcln request finished')
        resolve(response)
      })
      .catch((err) => {
        const response = err.response
        const elapsedTime = Date.now() - startTime
        const statusCode = response && response.status
        logger.error({ elapsedTime, statusCode, error: err.message, ...logData }, 'pcln request finished')
        reject(err)
      })
  })
}

module.exports = pclnRequest
